/**
 * Shared Google Analytics tracking functions
 * GA is initialized via each app's index.html, this module provides typed helpers.
 * App-specific events (e.g. CAT for THPT) should be added in each app's own telemetry file.
 */

const isBrowser = typeof window !== 'undefined';

// Minimal gtag type — compatible with both strict and loose declarations
type GtagFn = (...args: any[]) => void;

function getGtag(): GtagFn | null {
  if (!isBrowser) return null;
  return (window as any).gtag ?? null;
}

// ============================================
// EVENT TYPES
// ============================================

export interface ExamViewEvent {
  exam_id: string;
  exam_name: string;
  subject?: string;
  is_premium?: boolean;
  price?: number;
}

export interface ExamStartEvent {
  exam_id: string;
  exam_name: string;
  subject?: string;
  is_premium?: boolean;
}

export interface ExamSubmitEvent {
  exam_id: string;
  exam_name?: string;
  duration_seconds?: number;
  questions_answered?: number;
  total_questions?: number;
}

export interface ExamResultEvent {
  exam_id: string;
  score?: number;
  total_score?: number;
  duration_seconds?: number;
}

export interface ExamSearchEvent {
  search_term: string;
  results_count?: number;
}

export interface ExamFilterEvent {
  filter_type: 'subject' | 'level' | 'payment_type' | 'sort';
  filter_value: string;
}

export interface ExamPurchaseEvent {
  exam_id: string;
  exam_name: string;
  value: number;
  currency: string;
  transaction_id?: string;
}

// ============================================
// BASIC TRACKING
// ============================================

export function trackGAPageView(path?: string) {
  const gtag = getGtag();
  if (!gtag) return;

  if (path) {
    gtag('event', 'page_view', { page_path: path });
  } else {
    gtag('event', 'page_view');
  }
}

export function trackGAEvent(eventName: string, eventParams?: Record<string, unknown>) {
  const gtag = getGtag();
  if (!gtag) return;
  gtag('event', eventName, eventParams);
}

// ============================================
// USER IDENTITY
// ============================================

export function setGAUserId(userId: string) {
  const gtag = getGtag();
  if (!gtag) return;
  gtag('set', { user_id: userId });
}

// ============================================
// AUTH EVENTS
// ============================================

export function trackSignUp(method: 'email' | 'google' | 'facebook' = 'email') {
  trackGAEvent('sign_up', { method });
}

export function trackLogin(method: 'email' | 'google' | 'facebook' = 'email') {
  trackGAEvent('login', { method });
}

// ============================================
// EXAM FUNNEL EVENTS
// ============================================

export function trackExamView(params: ExamViewEvent) {
  trackGAEvent('exam_view', {
    exam_id: params.exam_id,
    exam_name: params.exam_name,
    subject: params.subject,
    is_premium: params.is_premium,
    price: params.price,
  });
}

export function trackExamStart(params: ExamStartEvent) {
  trackGAEvent('exam_start', {
    exam_id: params.exam_id,
    exam_name: params.exam_name,
    subject: params.subject,
    is_premium: params.is_premium,
  });
}

export function trackExamSubmit(params: ExamSubmitEvent) {
  trackGAEvent('exam_submit', {
    exam_id: params.exam_id,
    exam_name: params.exam_name,
    duration_seconds: params.duration_seconds,
    questions_answered: params.questions_answered,
    total_questions: params.total_questions,
  });
}

export function trackExamResult(params: ExamResultEvent) {
  trackGAEvent('exam_result_view', {
    exam_id: params.exam_id,
    score: params.score,
    total_score: params.total_score,
    duration_seconds: params.duration_seconds,
  });
}

export function trackExamExit(examId: string) {
  trackGAEvent('exam_exit', { exam_id: examId });
}

export function trackExamResume(examId: string) {
  trackGAEvent('exam_resume', { exam_id: examId });
}

export function trackExamPurchase(params: ExamPurchaseEvent) {
  trackGAEvent('purchase', {
    transaction_id: params.transaction_id || `exam_${params.exam_id}_${Date.now()}`,
    currency: params.currency,
    value: params.value,
    items: [
      {
        item_id: params.exam_id,
        item_name: params.exam_name,
        price: params.value,
        quantity: 1,
      },
    ],
  });
}

export function trackExamBeginCheckout(params: ExamPurchaseEvent) {
  trackGAEvent('begin_checkout', {
    currency: params.currency,
    value: params.value,
    items: [
      {
        item_id: params.exam_id,
        item_name: params.exam_name,
        price: params.value,
        quantity: 1,
      },
    ],
  });
}

// ============================================
// SEARCH & FILTER EVENTS
// ============================================

export function trackExamSearch(params: ExamSearchEvent) {
  trackGAEvent('search', {
    search_term: params.search_term,
    results_count: params.results_count,
  });
}

export function trackExamFilter(params: ExamFilterEvent) {
  trackGAEvent('exam_filter', {
    filter_type: params.filter_type,
    filter_value: params.filter_value,
  });
}

// ============================================
// ENGAGEMENT EVENTS
// ============================================

export function trackCTAClick(buttonName: string, location: string) {
  trackGAEvent('cta_click', {
    button_name: buttonName,
    location: location,
  });
}

export function trackScrollDepth(percent: number, pagePath: string) {
  trackGAEvent('scroll_depth', {
    percent: percent,
    page_path: pagePath,
  });
}

export function trackOutboundClick(url: string, linkText?: string) {
  trackGAEvent('outbound_click', {
    url: url,
    link_text: linkText,
  });
}

export function trackShare(contentType: string, itemId?: string) {
  trackGAEvent('share', {
    content_type: contentType,
    item_id: itemId,
  });
}

// ============================================
// SOCIAL PROOF EVENTS (Live Activity)
// ============================================

export function trackLiveToastImpression(examId: string, examName: string, activityType: string) {
  trackGAEvent('live_toast_impression', {
    exam_id: examId,
    exam_name: examName,
    activity_type: activityType,
  });
}

export function trackLiveToastClick(examId: string, examName: string) {
  trackGAEvent('live_toast_click', {
    exam_id: examId,
    exam_name: examName,
  });
}

export function trackLiveWidgetImpression(sessionCount: number) {
  trackGAEvent('live_widget_impression', {
    session_count: sessionCount,
  });
}

export function trackLiveWidgetClick(examId: string, examName: string) {
  trackGAEvent('live_widget_click', {
    exam_id: examId,
    exam_name: examName,
  });
}

// ============================================
// AUTO PAGE VIEW TRACKING
// ============================================

let _pageViewCleanup: (() => void) | null = null;

export function initAutoPageViewTracking(): () => void {
  if (!isBrowser || _pageViewCleanup) return () => {};

  let lastTrackedPath = window.location.pathname;
  trackGAPageView(lastTrackedPath);

  const trackIfPathChanged = () => {
    const currentPath = window.location.pathname;
    if (currentPath !== lastTrackedPath) {
      lastTrackedPath = currentPath;
      trackGAPageView(currentPath);
    }
  };

  const handlePopState = () => {
    trackIfPathChanged();
  };
  window.addEventListener('popstate', handlePopState);

  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function (...args) {
    originalPushState.apply(this, args);
    trackIfPathChanged();
  };

  history.replaceState = function (...args) {
    originalReplaceState.apply(this, args);
  };

  _pageViewCleanup = () => {
    window.removeEventListener('popstate', handlePopState);
    history.pushState = originalPushState;
    history.replaceState = originalReplaceState;
    _pageViewCleanup = null;
  };

  return _pageViewCleanup;
}

// ============================================
// SCROLL DEPTH TRACKING
// ============================================

export function initScrollDepthTracking(): () => void {
  if (!isBrowser) return () => {};

  const thresholds = [25, 50, 75, 100];
  const fired = new Set<number>();
  const pagePath = window.location.pathname;

  const handleScroll = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;

    const percent = Math.round((scrollTop / docHeight) * 100);

    for (const t of thresholds) {
      if (percent >= t && !fired.has(t)) {
        fired.add(t);
        trackScrollDepth(t, pagePath);
      }
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  return () => window.removeEventListener('scroll', handleScroll);
}
